import React, { useState, useMemo } from 'react'
// motion/AnimatePresence removed — polling causes flashing with animated cards
import { useLessonStore } from '@/store/lessonStore'
import {
  MessageSquare,
  Filter,
  Search,
  Clock,
  AlertCircle
} from 'lucide-react'
import type { PlayerResponse } from '@/types'

type FilterMode = 'all' | 'recent'

export function LiveResponses() {
  const { currentActivity, responses, players } = useLessonStore()
  const [filterMode, setFilterMode] = useState<FilterMode>('all')
  const [searchTerm, setSearchTerm] = useState('')


  const filteredResponses = useMemo(() => {
    let filtered = responses

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(response => {
        const player = players.find(p => p.id === response.player_id)
        const content = String(response.content).toLowerCase()
        const playerName = player?.display_name.toLowerCase() || ''
        return content.includes(searchTerm.toLowerCase()) || playerName.includes(searchTerm.toLowerCase())
      })
    }

    // Apply type filter
    if (filterMode !== 'all') {
      if (filterMode === 'recent') {
        const oneMinuteAgo = new Date(Date.now() - 60000)
        filtered = filtered.filter(response =>
          new Date(response.submitted_at) > oneMinuteAgo
        )
      }
    }

    // Sort by submission time (newest first)
    return filtered.sort((a, b) =>
      new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
    )
  }, [responses, searchTerm, filterMode, players])

  const responseStats = useMemo(() => {
    if (!currentActivity) return null

    const totalPlayers = players.filter(p => p.connection_status === 'connected').length
    const responseCount = responses.length
    const responseRate = totalPlayers > 0 ? (responseCount / totalPlayers) * 100 : 0

    return {
      totalPlayers,
      responseCount,
      responseRate,
      pendingCount: totalPlayers - responseCount
    }
  }, [currentActivity, players, responses])

  const getActivityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      mcq: 'Multiple Choice',
      coding: 'Blockly Coding',
      pdf_slideshow: 'PDF Slideshow',
      google_slides: 'Google Slides',
      command_shell: 'Command Shell',
      phishing_quiz: 'Phishing Quiz',
      packet_analysis: 'Packet Analysis',
      cyber_hygiene_quiz: 'Cyber Hygiene Quiz',
      coding_quiz: 'Coding Quiz',
      python_repl: 'Python Coding',
    }
    return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }

  const getPlayerName = (playerId: string) => {
    const player = players.find(p => p.id === playerId)
    return player?.display_name || 'Unknown Player'
  }

  const ResultBadge = ({ correct }: { correct: boolean }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
      correct ? 'bg-green-900/60 text-green-300' : 'bg-red-900/60 text-red-300'
    }`}>
      {correct ? '✓ Correct' : '✗ Incorrect'}
    </span>
  )

  const TestBadge = ({ passed, total }: { passed: number; total: number }) => {
    const allPassed = passed === total
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        allPassed ? 'bg-green-900/60 text-green-300' : 'bg-red-900/60 text-red-300'
      }`}>
        {allPassed ? '✓' : '✗'} {passed}/{total} passed
      </span>
    )
  }

  const formatResponseContent = (content: any, activityType?: string): React.ReactNode => {
    if (typeof content === 'string') return content
    if (typeof content !== 'object' || !content) return String(content)

    // MCQ
    if (activityType === 'mcq' && content.selected_option !== undefined) {
      return <span>Option {content.selected_option + 1}: {content.option_text || 'N/A'}</span>
    }

    // Phishing quiz
    if (activityType === 'phishing_quiz' && Array.isArray(content.answers) && content.answers.length > 0) {
      const answer = content.answers[0]
      const isCorrect = answer.correct === true
      const choice = String(answer.choice ?? '').toLowerCase()
      const label = choice.startsWith('phishing') ? 'Phishing' : choice.startsWith('legit') ? 'Legitimate' : answer.choice
      return <span><ResultBadge correct={isCorrect} /> <span className="text-theme-text">{label}</span></span>
    }

    // Quiz types with answers array (packet_analysis, cyber_hygiene_quiz, coding_quiz)
    if (Array.isArray(content.answers) && content.answers.length > 0) {
      const answer = content.answers[0]
      const isCorrect = answer.correct === true
      // Prefer option_text sent by the client (accounts for shuffled options)
      const optionText = answer.option_text ?? null
      const label = optionText
        ? `"${optionText}"`
        : answer.choice != null ? `Option ${answer.choice + 1}` : 'Answered'
      return <span><ResultBadge correct={isCorrect} /> <span className="text-theme-text">{label}</span></span>
    }

    // Command shell
    if (activityType === 'command_shell' && content.answer != null) {
      return <span><ResultBadge correct={content.correct === true} /> <code className="bg-surface-deep px-1.5 py-0.5 rounded text-sm">{content.answer}</code></span>
    }

    // Python REPL
    if (activityType === 'python_repl' && content.total_tests != null) {
      return <TestBadge passed={content.total_passed ?? 0} total={content.total_tests} />
    }

    // Coding (Blockly)
    if (activityType === 'coding' && content.execution_result) {
      const results = content.execution_result.test_results
      if (Array.isArray(results)) {
        const passed = results.filter((t: any) => t.passed).length
        return <TestBadge passed={passed} total={results.length} />
      }
    }

    return String(content.total_correct ?? content.answer ?? 'Submitted')
  }

  const ResponseCard = ({ response }: { response: PlayerResponse }) => {
    const player = players.find(p => p.id === response.player_id)

    return (
      <div className="bg-surface rounded-lg p-4 hover:bg-surface-hover transition-colors">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            {/* Player avatar */}
            {player?.avatar_url ? (
              <img
                src={player.avatar_url}
                alt={player.display_name}
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-theme-text font-bold text-sm">
                {getPlayerName(response.player_id).charAt(0).toUpperCase()}
              </div>
            )}

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">{getPlayerName(response.player_id)}</span>
                {response.score != null && (
                  <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                    response.score >= 80 ? 'bg-green-900 text-green-300' :
                    response.score >= 50 ? 'bg-yellow-900 text-yellow-300' :
                    'bg-red-900 text-red-300'
                  }`}>
                    {response.score}%
                  </span>
                )}
              </div>

              {/* Response content */}
              <div className="text-theme-text">
                {formatResponseContent(response.content, currentActivity?.activity_type)}
              </div>

              {/* Response metadata */}
              <div className="flex items-center gap-3 mt-2 text-xs text-theme-text-muted">
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(response.submitted_at).toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    )
  }

  return (
    <div className="bg-surface-deep rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[var(--color-primary)]" />
          <h3 className="ds-section-title">Live Responses</h3>
        </div>
      </div>

      {/* Response stats */}
      {responseStats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-surface rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-[var(--color-primary)]">{responseStats.responseCount}</div>
            <div className="text-sm text-theme-text-muted">Responses</div>
          </div>
          <div className="bg-surface rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-400">{Math.round(responseStats.responseRate)}%</div>
            <div className="text-sm text-theme-text-muted">Response Rate</div>
          </div>
          <div className="bg-surface rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-theme-text-muted">{responseStats.pendingCount}</div>
            <div className="text-sm text-theme-text-muted">Pending</div>
          </div>
          <div className="bg-surface rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-[var(--color-primary)]">{responseStats.totalPlayers}</div>
            <div className="text-sm text-theme-text-muted">Total Players</div>
          </div>
        </div>
      )}

      {/* Filters and search */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-theme-text-muted" />
          <input
            type="text"
            placeholder="Search responses or players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-hover border border-theme-border rounded-lg text-theme-text placeholder-theme-muted focus:border-blue-500 focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-theme-text-muted" />
          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value as FilterMode)}
            className="bg-surface-hover border border-theme-border rounded px-3 py-2 text-theme-text focus:border-blue-500 focus:outline-none"
          >
            <option value="all">All Responses</option>
            <option value="recent">Recent (1min)</option>
          </select>
        </div>
      </div>

      {/* Current activity info */}
      {currentActivity && (
        <div className="mb-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
          <div className="font-medium">{(currentActivity.activity_data as any)?.question || (currentActivity.activity_data as any)?.title || currentActivity.title}</div>
          <div className="text-sm text-theme-text-muted">
            {getActivityTypeLabel(currentActivity.activity_type)} · {responses.length} responses received
          </div>
        </div>
      )}

      {/* Responses list */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredResponses.length > 0 ? (
          filteredResponses.map(response => (
            <ResponseCard key={response.id} response={response} />
          ))
        ) : (
          <div className="text-center text-theme-text-muted py-8">
            {currentActivity ? (
              <>
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No responses yet</p>
                <p className="text-sm mt-1">Responses will appear here as players submit them</p>
              </>
            ) : (
              <>
                <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No active activity</p>
                <p className="text-sm mt-1">Start an activity to see responses</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}